import React, { useState, useRef, useEffect } from 'react';
import './URLBox.css';

function URLBox({ url, settings, onEdit, onDelete, onDragStart, onDragOver, onDragEnd, onDrop, isDragging, isDragOver }) {
  const [showDescription, setShowDescription] = useState(false);
  const hoverTimeoutRef = useRef(null);

  const handleClick = (e) => {
    // Don't open URL if clicking on action buttons or drag handle
    if (e.target.closest('.url-actions') || e.target.closest('.url-drag-handle')) {
      return;
    }
    let urlToOpen = url.url;
    if (!urlToOpen.startsWith('http://') && !urlToOpen.startsWith('https://')) {
      urlToOpen = 'https://' + urlToOpen;
    }
    window.open(urlToOpen, '_blank', 'noopener,noreferrer');
  };

  const handleMouseEnter = (e) => {
    // Don't trigger if hovering over action buttons
    if (e.target.closest('.url-actions')) {
      return;
    }
    // Only show description if it exists
    if (url.description && url.description.trim()) {
      hoverTimeoutRef.current = setTimeout(() => {
        setShowDescription(true);
      }, 1500); // 1.5 seconds
    }
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setShowDescription(false);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const cardStyle = {
    backgroundColor: settings.cardColor,
    color: settings.textColor,
    borderColor: settings.accentColor
  };

  const className = `url-box ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''} ${showDescription ? 'showing-description' : ''}`;
  const hasDescription = url.description && url.description.trim();

  return (
    <div 
      className={className}
      style={cardStyle}
      draggable
      onDragStart={(e) => onDragStart(e, url.id)}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver(e, url.id);
      }}
      onDragEnd={onDragEnd}
      onDrop={(e) => {
        e.preventDefault();
        onDrop(e, url.id);
      }}
      onMouseEnter={hasDescription ? handleMouseEnter : undefined}
      onMouseLeave={hasDescription ? handleMouseLeave : undefined}
    >
      <div className="url-actions">
        <span 
          className="url-drag-handle" 
          title="Drag to reorder"
          onMouseDown={(e) => e.stopPropagation()}
        >
          ☰
        </span>
        <button 
          className="url-action-button"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          title="Edit"
        >
          ✏️
        </button>
        <button 
          className="url-action-button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          title="Delete"
        >
          🗑️
        </button>
      </div>
      <div className="url-box-content" onClick={handleClick}>
        <div className="url-default-content">
          <div className="url-icon-container">
            {url.icon ? (
              <img 
                src={url.icon} 
                alt={url.name}
                className="url-icon"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
            ) : null}
            <div className="url-icon-fallback" style={{ display: url.icon ? 'none' : 'block' }}>
              {url.name ? url.name.charAt(0).toUpperCase() : '🌐'}
            </div>
          </div>
          <div className="url-text-content">
            <h3 className="url-title">{url.name || 'Untitled'}</h3>
          </div>
        </div>
        {hasDescription && (
          <div className="url-description-content">
            <p className="url-description">{url.description}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default URLBox;

